import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Adjust the import to match your Firebase configuration
import { getAuth } from 'firebase/auth'; // Import Firebase Authentication
import '../style/RTOinventory.css';
import * as XLSX from 'xlsx'; 

const AdminRTOInventoryPanel = () => {
    const [inventory, setInventory] = useState({});
    const [filteredInventory, setFilteredInventory] = useState({});
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(null); // Track which item is being deleted
    const [brands, setBrands] = useState([]); // List of unique brands
    const [selectedBrand, setSelectedBrand] = useState('all'); // Selected brand filter
    const [searchTerm, setSearchTerm] = useState(''); // Search term for SKU
    const [user, setUser] = useState(null); // Admin user
    const [allDesigns, setAllDesigns] = useState({}); // Store designs for all users
    const [downloading, setDownloading] = useState(false);

    const fetchCurrentUser = () => {
        const auth = getAuth();
        const currentUser = auth.currentUser;
        if (currentUser) {
            setUser(currentUser);
        } else {
            setUser(null);
        }
    };

    const fetchAllDesignsData = async () => {
        try {
            const usersRef = collection(db, 'users');
            const usersSnapshot = await getDocs(usersRef);
            const allDesignsData = {};

            // Fetch designs for each user
            for (const userDoc of usersSnapshot.docs) {
                const userId = userDoc.id;
                const designsRef = collection(db, 'users', userId, 'designs');
                const designsSnapshot = await getDocs(designsRef);
                
                allDesignsData[userId] = [];
                
                designsSnapshot.forEach(designDoc => {
                    const designData = designDoc.data();
                    if (designData.SKUs) {
                        allDesignsData[userId].push({
                            designId: designDoc.id,
                            frontImage: designData.images?.front,
                            backImage: designData.images?.back,
                            skus: designData.SKUs,
                        });
                    }
                });
            }

            setAllDesigns(allDesignsData);
        } catch (error) {
            console.error('Error fetching all designs data:', error);
        }
    };

    const fetchAllRTOInventory = async () => {
        try {
            const inventoryRef = collection(db, 'RTO_inventory');
            const inventorySnapshot = await getDocs(inventoryRef);
            const allInventory = {};
            const uniqueBrands = new Set();

            inventorySnapshot.forEach(docSnap => {
                const itemData = docSnap.data();
                const docId = docSnap.id;
                
                // Extract data from the new structure
                const sku = itemData.sku;
                const brandId = itemData.brandId;
                const units = itemData.units || 0;
                const itemName = itemData.itemName;
                const createdAt = itemData.createdAt;
                const updatedAt = itemData.updatedAt;
                const originalOrderId = itemData.originalOrderId;
                
                // Add brand to unique brands set
                if (brandId) {
                    uniqueBrands.add(brandId);
                }
                
                // Only add to inventory if units > 0
                if (units > 0 && sku) {
                    // Extract size from SKU (assuming format like "u-48554-XL")
                    const skuParts = sku.split('-');
                    const size = skuParts[skuParts.length - 1]; // Last part is the size
                    
                    // Create or update inventory item
                    if (!allInventory[sku]) {
                        allInventory[sku] = {
                            sku: sku,
                            brandId: brandId,
                            counts: {},
                            frontImage: null,
                            backImage: null,
                            docId: docId,
                            itemName: itemName,
                            createdAt: createdAt,
                            updatedAt: updatedAt,
                            originalOrderId: originalOrderId
                        };
                    }
                    
                    // Set the count for this size
                    allInventory[sku].counts[size] = units;
                }
            });

            // Map images from designs to inventory items
            Object.keys(allInventory).forEach(sku => {
                const item = allInventory[sku];
                const brandId = item.brandId;

                if (allDesigns[brandId]) {
                    allDesigns[brandId].forEach(design => {
                        design.skus.forEach(designSKU => {
                            // Extract base SKU for comparison (without size)
                            const inventoryBase = sku.substring(0, sku.lastIndexOf('-'));
                            const designBase = designSKU.substring(0, designSKU.lastIndexOf('-'));
                            
                            if (inventoryBase === designBase) {
                                allInventory[sku].frontImage = design.frontImage;
                                allInventory[sku].backImage = design.backImage;
                            }
                        });
                    });
                }
            });

            setInventory(allInventory);
            setFilteredInventory(allInventory);
            setBrands(Array.from(uniqueBrands));
            setLoading(false);
        } catch (error) {
            console.error('Error fetching all RTO inventory:', error);
            setLoading(false);
        }
    };

    const handleDeleteInventoryItem = async (sku, docId) => {
        const confirmDelete = window.confirm(`Are you sure you want to delete inventory item ${sku}?`);
        
        if (!confirmDelete) return;

        setDeleting(sku);
        
        try {
            await deleteDoc(doc(db, 'RTO_inventory', docId));
            
            // Remove from local state
            const updatedInventory = { ...inventory };
            delete updatedInventory[sku];
            setInventory(updatedInventory);
            
            // Update filtered inventory as well
            const updatedFilteredInventory = { ...filteredInventory };
            delete updatedFilteredInventory[sku];
            setFilteredInventory(updatedFilteredInventory);
            
            console.log(`Successfully deleted inventory item: ${sku}`);
        } catch (error) {
            console.error('Error deleting inventory item:', error);
            alert('Failed to delete inventory item. Please try again.');
        } finally {
            setDeleting(null);
        }
    };

    const applyFilters = () => {
        let filtered = { ...inventory };

        // Brand filter
        if (selectedBrand !== 'all') {
            filtered = Object.keys(filtered).reduce((acc, key) => {
                if (filtered[key].brandId === selectedBrand) {
                    acc[key] = filtered[key];
                }
                return acc;
            }, {});
        }

        // Search filter
        if (searchTerm.trim()) {
            filtered = Object.keys(filtered).reduce((acc, key) => {
                if (key.toLowerCase().includes(searchTerm.toLowerCase())) {
                    acc[key] = filtered[key];
                }
                return acc;
            }, {});
        }

        setFilteredInventory(filtered);
    };

    const handleDownloadExcel = async () => {
        setDownloading(true);
        
        try {
            // Prepare data for Excel
            const excelData = [];
            
            Object.values(filteredInventory).forEach(item => {
                // Create a row with basic info and size counts
                const row = {
                    SKU: item.sku,
                    'Item Name': item.itemName || 'N/A',
                    'Brand ID': item.brandId,
                    'Units': Object.values(item.counts).reduce((sum, count) => sum + count, 0),
                    'Created At': item.createdAt || 'N/A',
                    'Updated At': item.updatedAt || 'N/A',
                    'Original Order ID': item.originalOrderId || 'N/A',
                    'Front Image URL': item.frontImage || 'N/A',
                    'Back Image URL': item.backImage || 'N/A'
                };
                
                // Add size counts as separate columns
                Object.keys(item.counts).forEach(size => {
                    row[`Size ${size}`] = item.counts[size];
                });
                
                excelData.push(row);
            });

            // Create workbook and worksheet
            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.json_to_sheet(excelData);
            
            // Auto-size columns
            const colWidths = [];
            const headers = Object.keys(excelData[0] || {});
            headers.forEach((header, index) => {
                const maxLength = Math.max(
                    header.length,
                    ...excelData.map(row => String(row[header] || '').length)
                );
                colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
            });
            worksheet['!cols'] = colWidths;
            
            // Add worksheet to workbook
            XLSX.utils.book_append_sheet(workbook, worksheet, 'RTO Inventory');
            
            // Generate filename with current date
            const today = new Date();
            const dateStr = today.toISOString().split('T')[0]; // YYYY-MM-DD format
            const filename = `RTO_Inventory_${dateStr}.xlsx`;
            
            // Download the file
            XLSX.writeFile(workbook, filename);
            
            console.log('Excel file downloaded successfully');
        } catch (error) {
            console.error('Error generating Excel file:', error);
            alert('Failed to download Excel file. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const handleBrandFilterChange = (e) => {
        setSelectedBrand(e.target.value);
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const clearFilters = () => {
        setSelectedBrand('all');
        setSearchTerm('');
        setFilteredInventory(inventory);
    };

    // Calculate total inventory count
    const getTotalInventoryCount = () => {
        return Object.values(filteredInventory).reduce((total, item) => {
            return total + Object.values(item.counts).reduce((sum, count) => sum + count, 0);
        }, 0);
    };

    useEffect(() => {
        fetchCurrentUser();
    }, []);

    useEffect(() => {
        if (user) {
            fetchAllDesignsData();
        }
    }, [user]);

    useEffect(() => {
        if (Object.keys(allDesigns).length > 0) {
            fetchAllRTOInventory();
        }
    }, [allDesigns]);

    useEffect(() => {
        applyFilters();
    }, [selectedBrand, searchTerm, inventory]);

    return (
        <div className="dashboard">
            <div className="dashboard-container">
                <div className="main-content">
                    <h1>Admin RTO Inventory Management</h1>

                    <div className="download-section" style={{ marginBottom: '20px' }}>
                        <button 
                            onClick={handleDownloadExcel} 
                            disabled={downloading || loading || Object.keys(filteredInventory).length === 0}
                            className="download-excel-btn"
                            style={{
                                padding: '12px 24px',
                                backgroundColor: downloading ? '#ccc' : '#28a745',
                                color: 'white',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: downloading || loading ? 'not-allowed' : 'pointer',
                                fontSize: '16px',
                                fontWeight: 'bold'
                            }}
                        >
                            {downloading ? 'Downloading...' : 'Download Excel'}
                        </button>
                        {Object.keys(filteredInventory).length === 0 && !loading && (
                            <span style={{ marginLeft: '10px', color: '#666', fontSize: '14px' }}>
                                No data available to download
                            </span>
                        )}
                    </div>

                    {/* Filters Section */}
                    <div className="filters-section">
                        <div className="filters-container">
                            <div className="filter-group">
                                <label htmlFor="brandFilter">Filter by Brand:</label>
                                <select
                                    id="brandFilter"
                                    value={selectedBrand}
                                    onChange={handleBrandFilterChange}
                                    className="filter-select"
                                >
                                    <option value="all">All Brands</option>
                                    {brands.map(brand => (
                                        <option key={brand} value={brand}>
                                            {brand}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="filter-group">
                                <label htmlFor="searchSku">Search SKU:</label>
                                <input
                                    id="searchSku"
                                    type="text"
                                    value={searchTerm}
                                    onChange={handleSearchChange}
                                    placeholder="Enter SKU to search..."
                                    className="filter-input"
                                />
                            </div>

                            <button onClick={clearFilters} className="clear-filters-btn">
                                Clear Filters
                            </button>
                        </div>

                        <div className="inventory-stats">
                            Showing {Object.keys(filteredInventory).length} items 
                            | Total Inventory Count: {getTotalInventoryCount()}
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-message">
                            <p>Loading all inventory...</p>
                        </div>
                    ) : (
                        <div className="inventory-cards">
                            {Object.keys(filteredInventory).length > 0 ? (
                                Object.values(filteredInventory).map(item => (
                                    <div key={item.sku} className={`inventory-card ${deleting === item.sku ? 'deleting' : ''}`}>
                                        {/* Delete Button */}
                                        <button
                                            onClick={() => handleDeleteInventoryItem(item.sku, item.docId)}
                                            disabled={deleting === item.sku}
                                            className="delete-btn"
                                        >
                                            {deleting === item.sku ? 'Deleting...' : 'Delete'}
                                        </button>

                                        <div className="inventory-images">
                                            <div className="image-container">
                                                {item.frontImage ? (
                                                    <img src={item.frontImage} alt="front" className="image" />
                                                ) : (
                                                    <p>No front image</p>
                                                )}
                                            </div>
                                            <div className="image-container">
                                                {item.backImage ? (
                                                    <img src={item.backImage} alt="back" className="image" />
                                                ) : (
                                                    <p>No back image</p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="inventory-details">
                                            <p><strong>SKU:</strong> {item.sku}</p>
                                            <p><strong>Item Name:</strong> {item.itemName || 'N/A'}</p>
                                            <p><strong>Brand ID:</strong> {item.brandId}</p>
                                            <div className="size-counts">
                                                {Object.keys(item.counts).map(size => (
                                                    <p key={size}>
                                                        <strong>{size}:</strong> {item.counts[size]}
                                                    </p>
                                                ))}
                                            </div>
                                            {item.createdAt && (
                                                <p><strong>Created:</strong> {new Date(item.createdAt).toLocaleDateString()}</p>
                                            )}
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state">
                                    <p>No RTO inventory found matching the current filters.</p>
                                    {(selectedBrand !== 'all' || searchTerm.trim()) && (
                                        <button onClick={clearFilters} className="show-all-btn">
                                            Clear Filters to Show All
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminRTOInventoryPanel;

// import { migrateRTOInventory, migrateRTOInventorySafe, previewMigration } from './MigrationScript'; // Adjust path

// const MigrationPanel = () => {
//     const [isProcessing, setIsProcessing] = useState(false);
//     const [previewData, setPreviewData] = useState(null);

//     const handlePreview = async () => {
//         setIsProcessing(true);
//         try {
//             const preview = await previewMigration();
//             setPreviewData(preview);
//         } catch (error) {
//             console.error('Preview failed:', error);
//         } finally {
//             setIsProcessing(false);
//         }
//     };

//     const handleSafeMigration = async () => {
//         const confirm = window.confirm(
//             'This will create new documents with the new format while keeping old data. Continue?'
//         );
        
//         if (!confirm) return;
        
//         setIsProcessing(true);
//         try {
//             await migrateRTOInventorySafe();
//             setPreviewData(null); // Clear preview after migration
//         } catch (error) {
//             console.error('Safe migration failed:', error);
//         } finally {
//             setIsProcessing(false);
//         }
//     };

//     const handleFullMigration = async () => {
//         const confirm = window.confirm(
//             'This will migrate data and DELETE old format documents. Are you sure? This cannot be undone!'
//         );
        
//         if (!confirm) return;
        
//         setIsProcessing(true);
//         try {
//             await migrateRTOInventory();
//             setPreviewData(null); // Clear preview after migration
//         } catch (error) {
//             console.error('Full migration failed:', error);
//         } finally {
//             setIsProcessing(false);
//         }
//     };

//     return (
//         <div style={{ padding: '20px', border: '1px solid #ccc', margin: '20px 0' }}>
//             <h2>RTO Inventory Data Migration</h2>
            
//             <div style={{ marginBottom: '20px' }}>
//                 <h3>Migration Options:</h3>
//                 <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
//                     <button 
//                         onClick={handlePreview}
//                         disabled={isProcessing}
//                         style={{
//                             padding: '10px 15px',
//                             backgroundColor: '#007bff',
//                             color: 'white',
//                             border: 'none',
//                             borderRadius: '5px',
//                             cursor: isProcessing ? 'not-allowed' : 'pointer'
//                         }}
//                     >
//                         {isProcessing ? 'Processing...' : 'Preview Migration'}
//                     </button>
                    
//                     <button 
//                         onClick={handleSafeMigration}
//                         disabled={isProcessing}
//                         style={{
//                             padding: '10px 15px',
//                             backgroundColor: '#28a745',
//                             color: 'white',
//                             border: 'none',
//                             borderRadius: '5px',
//                             cursor: isProcessing ? 'not-allowed' : 'pointer'
//                         }}
//                     >
//                         {isProcessing ? 'Processing...' : 'Safe Migration (Keep Old Data)'}
//                     </button>
                    
//                     <button 
//                         onClick={handleFullMigration}
//                         disabled={isProcessing}
//                         style={{
//                             padding: '10px 15px',
//                             backgroundColor: '#dc3545',
//                             color: 'white',
//                             border: 'none',
//                             borderRadius: '5px',
//                             cursor: isProcessing ? 'not-allowed' : 'pointer'
//                         }}
//                     >
//                         {isProcessing ? 'Processing...' : 'Full Migration (Delete Old Data)'}
//                     </button>
//                 </div>
//             </div>

//             {previewData && (
//                 <div style={{ marginTop: '20px' }}>
//                     <h3>Migration Preview:</h3>
//                     <p><strong>Total items to migrate:</strong> {previewData.length}</p>
                    
//                     <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', padding: '10px' }}>
//                         <table style={{ width: '100%', borderCollapse: 'collapse' }}>
//                             <thead>
//                                 <tr style={{ backgroundColor: '#f8f9fa' }}>
//                                     <th style={{ border: '1px solid #ddd', padding: '8px' }}>Old Doc ID</th>
//                                     <th style={{ border: '1px solid #ddd', padding: '8px' }}>New SKU</th>
//                                     <th style={{ border: '1px solid #ddd', padding: '8px' }}>Units</th>
//                                     <th style={{ border: '1px solid #ddd', padding: '8px' }}>Brand ID</th>
//                                 </tr>
//                             </thead>
//                             <tbody>
//                                 {previewData.slice(0, 50).map((item, index) => (
//                                     <tr key={index}>
//                                         <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.oldDocId}</td>
//                                         <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.newSku}</td>
//                                         <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.units}</td>
//                                         <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.brandId}</td>
//                                     </tr>
//                                 ))}
//                             </tbody>
//                         </table>
//                         {previewData.length > 50 && (
//                             <p style={{ textAlign: 'center', marginTop: '10px' }}>
//                                 Showing first 50 of {previewData.length} items
//                             </p>
//                         )}
//                     </div>
//                 </div>
//             )}

//             <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '5px' }}>
//                 <h4>Migration Explanation:</h4>
//                 <ul>
//                     <li><strong>Preview:</strong> Shows what will be migrated without making changes</li>
//                     <li><strong>Safe Migration:</strong> Creates new format documents while keeping old ones as backup</li>
//                     <li><strong>Full Migration:</strong> Migrates data and deletes old format documents (recommended after testing)</li>
//                 </ul>
                
//                 <h4>Data Transformation:</h4>
//                 <p>Old format: Document ID = SKU (e.g., "u-328707"), Data = {'{size: count, brandId: "..."}'}</p>
//                 <p>New format: Auto-generated ID, Data = {'{sku: "u-328707-M", units: count, brandId: "...", createdAt: "...", updatedAt: "..."}'}</p>
//             </div>
//         </div>
//     );
// };

// export default MigrationPanel;


// import React, { useEffect, useState } from 'react';
// import { collection, getDocs, query, where, doc, deleteDoc, setDoc, updateDoc } from 'firebase/firestore';
// import { db } from '../firebase'; // Adjust the import to match your Firebase configuration
// import { getAuth } from 'firebase/auth'; // Import Firebase Authentication
// import '../style/RTOinventory.css';

// const AdminRTOInventoryPanel = () => {
//     const [inventory, setInventory] = useState({});
//     const [filteredInventory, setFilteredInventory] = useState({});
//     const [loading, setLoading] = useState(true);
//     const [deleting, setDeleting] = useState(null); // Track which item is being deleted
//     const [brands, setBrands] = useState([]); // List of unique brands
//     const [selectedBrand, setSelectedBrand] = useState('all'); // Selected brand filter
//     const [searchTerm, setSearchTerm] = useState(''); // Search term for SKU
//     const [user, setUser] = useState(null); // Admin user
//     const [allDesigns, setAllDesigns] = useState({}); // Store designs for all users
//     const [editingItem, setEditingItem] = useState(null); // Track which item is being edited
//     const [editValues, setEditValues] = useState({}); // Store edit values
//     const [showAddForm, setShowAddForm] = useState(false); // Show add new product form
//     const [newProduct, setNewProduct] = useState({
//         sku: '',
//         brandId: '',
//         sizes: {},
//         customFields: {}
//     });
//     const [showZeroInventory, setShowZeroInventory] = useState(true); // Toggle to show/hide zero inventory
//     const [customFieldDefinitions, setCustomFieldDefinitions] = useState([]); // Define custom fields
//     const [newFieldName, setNewFieldName] = useState('');
//     const [newFieldType, setNewFieldType] = useState('text');
//     const [showFieldManager, setShowFieldManager] = useState(false);

//     // Common sizes for new products
//     const commonSizes = ['XS-black', 'XS-white', 'S-black', 'S-white', 'M-black', 'M-white', 'L-black', 'L-white', 'XL-black', 'XL-white', 'XXL-black', 'XXL-white'];
    
//     // Field types for custom fields
//     const fieldTypes = [
//         { value: 'text', label: 'Text' },
//         { value: 'number', label: 'Number' },
//         { value: 'boolean', label: 'Yes/No' },
//         { value: 'select', label: 'Dropdown' },
//         { value: 'date', label: 'Date' },
//         { value: 'textarea', label: 'Long Text' }
//     ];

//     const fetchCurrentUser = () => {
//         const auth = getAuth();
//         const currentUser = auth.currentUser;
//         if (currentUser) {
//             setUser(currentUser);
//         } else {
//             setUser(null);
//         }
//     };

//     const fetchAllDesignsData = async () => {
//         try {
//             const usersRef = collection(db, 'users');
//             const usersSnapshot = await getDocs(usersRef);
//             const allDesignsData = {};

//             // Fetch designs for each user
//             for (const userDoc of usersSnapshot.docs) {
//                 const userId = userDoc.id;
//                 const designsRef = collection(db, 'users', userId, 'designs');
//                 const designsSnapshot = await getDocs(designsRef);
                
//                 allDesignsData[userId] = [];
                
//                 designsSnapshot.forEach(designDoc => {
//                     const designData = designDoc.data();
//                     if (designData.SKUs) {
//                         allDesignsData[userId].push({
//                             designId: designDoc.id,
//                             frontImage: designData.images?.front,
//                             backImage: designData.images?.back,
//                             skus: designData.SKUs,
//                         });
//                     }
//                 });
//             }

//             setAllDesigns(allDesignsData);
//         } catch (error) {
//             console.error('Error fetching all designs data:', error);
//         }
//     };

//     const fetchAllRTOInventory = async () => {
//         try {
//             const inventoryRef = collection(db, 'RTO_inventory');
//             const inventorySnapshot = await getDocs(inventoryRef);
//             const allInventory = {};
//             const uniqueBrands = new Set();

//             inventorySnapshot.forEach(docSnap => {
//                 const skuData = docSnap.data();
//                 const skuPrefix = docSnap.id;
//                 const brandId = skuData.brandId;
                
//                 // Add brand to unique brands set
//                 if (brandId) {
//                     uniqueBrands.add(brandId);
//                 }
                
//                 // Include all inventory items, regardless of count
//                 allInventory[skuPrefix] = {
//                     sku: skuPrefix,
//                     brandId: brandId,
//                     counts: {},
//                     customFields: {},
//                     frontImage: null,
//                     backImage: null,
//                     docId: docSnap.id, // Store document ID for operations
//                 };

//                 Object.keys(skuData).forEach(key => {
//                     if (key === 'brandId') {
//                         // Skip brandId as it's already handled
//                     } else if (commonSizes.includes(key) || /^(XS|S|M|L|XL|XXL|XXXL|\d+)$/i.test(key)) {
//                         // Handle size fields
//                         allInventory[skuPrefix].counts[key] = skuData[key] || 0;
//                     } else {
//                         // Handle custom fields
//                         allInventory[skuPrefix].customFields[key] = skuData[key];
//                     }
//                 });
//             });

//             // Map images from designs to inventory items
//             Object.keys(allInventory).forEach(sku => {
//                 const item = allInventory[sku];
//                 const skuBase = sku.split('-')[0] + '-' + sku.split('-')[1];
//                 const brandId = item.brandId;

//                 if (allDesigns[brandId]) {
//                     allDesigns[brandId].forEach(design => {
//                         design.skus.forEach(designSKU => {
//                             const designBase = designSKU.split('-')[0] + '-' + designSKU.split('-')[1];
//                             if (skuBase === designBase) {
//                                 allInventory[sku].frontImage = design.frontImage;
//                                 allInventory[sku].backImage = design.backImage;
//                             }
//                         });
//                     });
//                 }
//             });

//             setInventory(allInventory);
//             setFilteredInventory(allInventory);
//             setBrands(Array.from(uniqueBrands));
//             setLoading(false);
//         } catch (error) {
//             console.error('Error fetching all RTO inventory:', error);
//             setLoading(false);
//         }
//     };

//     const handleDeleteInventoryItem = async (sku, docId) => {
//         const confirmDelete = window.confirm(`Are you sure you want to delete inventory item ${sku}?`);
        
//         if (!confirmDelete) return;

//         setDeleting(sku);
        
//         try {
//             await deleteDoc(doc(db, 'RTO_inventory', docId));
            
//             // Remove from local state
//             const updatedInventory = { ...inventory };
//             delete updatedInventory[sku];
//             setInventory(updatedInventory);
            
//             // Update filtered inventory as well
//             const updatedFilteredInventory = { ...filteredInventory };
//             delete updatedFilteredInventory[sku];
//             setFilteredInventory(updatedFilteredInventory);
            
//             console.log(`Successfully deleted inventory item: ${sku}`);
//         } catch (error) {
//             console.error('Error deleting inventory item:', error);
//             alert('Failed to delete inventory item. Please try again.');
//         } finally {
//             setDeleting(null);
//         }
//     };

//     const handleDeleteSize = async (sku, size) => {
//         const confirmDelete = window.confirm(`Are you sure you want to delete size ${size} from ${sku}?`);
        
//         if (!confirmDelete) return;

//         try {
//             const itemRef = doc(db, 'RTO_inventory', sku);
//             const updatedCounts = { ...inventory[sku].counts };
//             delete updatedCounts[size];

//             // Update Firestore
//             await updateDoc(itemRef, {
//                 [size]: null // This will remove the field
//             });

//             // Update local state
//             const updatedInventory = { ...inventory };
//             updatedInventory[sku].counts = updatedCounts;
//             setInventory(updatedInventory);
            
//             console.log(`Successfully deleted size ${size} from ${sku}`);
//             applyFilters();
//         } catch (error) {
//             console.error('Error deleting size:', error);
//             alert('Failed to delete size. Please try again.');
//         }
//     };

//     const handleEditStart = (sku) => {
//         setEditingItem(sku);
//         setEditValues({ ...inventory[sku].counts });
//     };

//     const handleEditCancel = () => {
//         setEditingItem(null);
//         setEditValues({});
//     };

//     const handleEditSave = async (sku) => {
//         try {
//             const itemRef = doc(db, 'RTO_inventory', sku);
            
//             // Prepare update data with both sizes and custom fields
//             const updateData = {
//                 ...editValues,
//                 ...inventory[sku].customFields
//             };
            
//             // Update Firestore with new values
//             await updateDoc(itemRef, updateData);

//             // Update local state
//             const updatedInventory = { ...inventory };
//             updatedInventory[sku].counts = { ...editValues };
//             setInventory(updatedInventory);
            
//             setEditingItem(null);
//             setEditValues({});
//             console.log(`Successfully updated ${sku}`);
//             applyFilters();
//         } catch (error) {
//             console.error('Error updating inventory:', error);
//             alert('Failed to update inventory. Please try again.');
//         }
//     };

//     const handleEditValueChange = (size, value) => {
//         setEditValues(prev => ({
//             ...prev,
//             [size]: parseInt(value) || 0
//         }));
//     };

//     const handleAddNewSize = (sku, newSize) => {
//         if (newSize && !inventory[sku].counts.hasOwnProperty(newSize)) {
//             setEditValues(prev => ({
//                 ...prev,
//                 [newSize]: 0
//             }));
//         }
//     };

//     const handleAddNewProduct = async () => {
//         if (!newProduct.sku || !newProduct.brandId) {
//             alert('Please fill in SKU and Brand ID');
//             return;
//         }

//         try {
//             const docRef = doc(db, 'RTO_inventory', newProduct.sku);
//             const productData = {
//                 brandId: newProduct.brandId,
//                 ...newProduct.sizes,
//                 ...newProduct.customFields
//             };

//             await setDoc(docRef, productData);

//             // Add to local state
//             const updatedInventory = {
//                 ...inventory,
//                 [newProduct.sku]: {
//                     sku: newProduct.sku,
//                     brandId: newProduct.brandId,
//                     counts: { ...newProduct.sizes },
//                     customFields: { ...newProduct.customFields },
//                     frontImage: null,
//                     backImage: null,
//                     docId: newProduct.sku
//                 }
//             };

//             setInventory(updatedInventory);
            
//             // Reset form
//             setNewProduct({
//                 sku: '',
//                 brandId: '',
//                 sizes: {},
//                 customFields: {}
//             });
//             setShowAddForm(false);
            
//             console.log(`Successfully added new product: ${newProduct.sku}`);
//             applyFilters();
//         } catch (error) {
//             console.error('Error adding new product:', error);
//             alert('Failed to add new product. Please try again.');
//         }
//     };

//     const handleNewProductSizeChange = (size, value) => {
//         setNewProduct(prev => ({
//             ...prev,
//             sizes: {
//                 ...prev.sizes,
//                 [size]: parseInt(value) || 0
//             }
//         }));
//     };

//     const handleNewProductCustomFieldChange = (fieldName, value, fieldType) => {
//         let processedValue = value;
        
//         switch (fieldType) {
//             case 'number':
//                 processedValue = parseFloat(value) || 0;
//                 break;
//             case 'boolean':
//                 processedValue = value === 'true';
//                 break;
//             case 'date':
//                 processedValue = value;
//                 break;
//             default:
//                 processedValue = value;
//         }

//         setNewProduct(prev => ({
//             ...prev,
//             customFields: {
//                 ...prev.customFields,
//                 [fieldName]: processedValue
//             }
//         }));
//     };

//     const handleAddCustomField = () => {
//         if (!newFieldName.trim()) {
//             alert('Please enter a field name');
//             return;
//         }

//         const newField = {
//             name: newFieldName.trim(),
//             type: newFieldType,
//             id: Date.now().toString()
//         };

//         // If it's a select field, add options
//         if (newFieldType === 'select') {
//             newField.options = ['Option 1', 'Option 2', 'Option 3'];
//         }

//         setCustomFieldDefinitions(prev => [...prev, newField]);
//         setNewFieldName('');
//         setNewFieldType('text');
//     };

//     const handleRemoveCustomField = (fieldId) => {
//         setCustomFieldDefinitions(prev => prev.filter(field => field.id !== fieldId));
        
//         // Remove from all products
//         const updatedInventory = { ...inventory };
//         Object.keys(updatedInventory).forEach(sku => {
//             const fieldToRemove = customFieldDefinitions.find(f => f.id === fieldId);
//             if (fieldToRemove && updatedInventory[sku].customFields) {
//                 delete updatedInventory[sku].customFields[fieldToRemove.name];
//             }
//         });
//         setInventory(updatedInventory);
//     };

//     const handleUpdateFieldOptions = (fieldId, options) => {
//         setCustomFieldDefinitions(prev => 
//             prev.map(field => 
//                 field.id === fieldId 
//                     ? { ...field, options: options.split(',').map(opt => opt.trim()) }
//                     : field
//             )
//         );
//     };

//     const renderCustomFieldInput = (field, value, onChange) => {
//         switch (field.type) {
//             case 'text':
//                 return (
//                     <input
//                         type="text"
//                         value={value || ''}
//                         onChange={(e) => onChange(field.name, e.target.value, field.type)}
//                         placeholder={`Enter ${field.name}`}
//                     />
//                 );
//             case 'number':
//                 return (
//                     <input
//                         type="number"
//                         value={value || 0}
//                         onChange={(e) => onChange(field.name, e.target.value, field.type)}
//                         placeholder={`Enter ${field.name}`}
//                     />
//                 );
//             case 'boolean':
//                 return (
//                     <select
//                         value={value === undefined ? '' : value.toString()}
//                         onChange={(e) => onChange(field.name, e.target.value, field.type)}
//                     >
//                         <option value="">Select...</option>
//                         <option value="true">Yes</option>
//                         <option value="false">No</option>
//                     </select>
//                 );
//             case 'select':
//                 return (
//                     <select
//                         value={value || ''}
//                         onChange={(e) => onChange(field.name, e.target.value, field.type)}
//                     >
//                         <option value="">Select...</option>
//                         {field.options?.map(option => (
//                             <option key={option} value={option}>{option}</option>
//                         ))}
//                     </select>
//                 );
//             case 'date':
//                 return (
//                     <input
//                         type="date"
//                         value={value || ''}
//                         onChange={(e) => onChange(field.name, e.target.value, field.type)}
//                     />
//                 );
//             case 'textarea':
//                 return (
//                     <textarea
//                         value={value || ''}
//                         onChange={(e) => onChange(field.name, e.target.value, field.type)}
//                         placeholder={`Enter ${field.name}`}
//                         rows="3"
//                     />
//                 );
//             default:
//                 return null;
//         }
//     };

//     const applyFilters = () => {
//         let filtered = { ...inventory };

//         // Brand filter
//         if (selectedBrand !== 'all') {
//             filtered = Object.keys(filtered).reduce((acc, key) => {
//                 if (filtered[key].brandId === selectedBrand) {
//                     acc[key] = filtered[key];
//                 }
//                 return acc;
//             }, {});
//         }

//         // Search filter
//         if (searchTerm.trim()) {
//             filtered = Object.keys(filtered).reduce((acc, key) => {
//                 if (key.toLowerCase().includes(searchTerm.toLowerCase())) {
//                     acc[key] = filtered[key];
//                 }
//                 return acc;
//             }, {});
//         }

//         // Zero inventory filter
//         if (!showZeroInventory) {
//             filtered = Object.keys(filtered).reduce((acc, key) => {
//                 const hasNonZeroCount = Object.values(filtered[key].counts).some(count => count > 0);
//                 if (hasNonZeroCount) {
//                     acc[key] = filtered[key];
//                 }
//                 return acc;
//             }, {});
//         }

//         setFilteredInventory(filtered);
//     };

//     const handleBrandFilterChange = (e) => {
//         setSelectedBrand(e.target.value);
//     };

//     const handleSearchChange = (e) => {
//         setSearchTerm(e.target.value);
//     };

//     const clearFilters = () => {
//         setSelectedBrand('all');
//         setSearchTerm('');
//         setShowZeroInventory(true);
//         setFilteredInventory(inventory);
//     };

//     // Calculate total inventory count
//     const getTotalInventoryCount = () => {
//         return Object.values(filteredInventory).reduce((total, item) => {
//             return total + Object.values(item.counts).reduce((sum, count) => sum + count, 0);
//         }, 0);
//     };

//     const hasZeroInventory = (item) => {
//         return Object.values(item.counts).every(count => count === 0);
//     };

//     useEffect(() => {
//         fetchCurrentUser();
//     }, []);

//     useEffect(() => {
//         if (user) {
//             fetchAllDesignsData();
//         }
//     }, [user]);

//     useEffect(() => {
//         if (Object.keys(allDesigns).length > 0) {
//             fetchAllRTOInventory();
//         }
//     }, [allDesigns]);

//     useEffect(() => {
//         applyFilters();
//     }, [selectedBrand, searchTerm, inventory, showZeroInventory]);

//     return (
//         <div className="dashboard">
//             <div className="dashboard-container">
//                 <div className="main-content">
//                     <h1>Admin RTO Inventory Management</h1>

//                     {/* Custom Field Manager Button */}
//                     <div className="action-buttons">
//                         <button 
//                             onClick={() => setShowAddForm(!showAddForm)} 
//                             className="add-product-btn"
//                         >
//                             {showAddForm ? 'Cancel' : 'Add New RTO Product'}
//                         </button>
//                         <button 
//                             onClick={() => setShowFieldManager(!showFieldManager)} 
//                             className="field-manager-btn"
//                         >
//                             {showFieldManager ? 'Close Field Manager' : 'Manage Custom Fields'}
//                         </button>
//                     </div>

//                     {/* Custom Field Manager */}
//                     {showFieldManager && (
//                         <div className="field-manager">
//                             <h3>Custom Field Manager</h3>
                            
//                             {/* Add New Field */}
//                             <div className="add-field-section">
//                                 <h4>Add New Field</h4>
//                                 <div className="add-field-form">
//                                     <input
//                                         type="text"
//                                         placeholder="Field Name"
//                                         value={newFieldName}
//                                         onChange={(e) => setNewFieldName(e.target.value)}
//                                     />
//                                     <select
//                                         value={newFieldType}
//                                         onChange={(e) => setNewFieldType(e.target.value)}
//                                     >
//                                         {fieldTypes.map(type => (
//                                             <option key={type.value} value={type.value}>
//                                                 {type.label}
//                                             </option>
//                                         ))}
//                                     </select>
//                                     <button onClick={handleAddCustomField} className="add-field-btn">
//                                         Add Field
//                                     </button>
//                                 </div>
//                             </div>

//                             {/* Existing Fields */}
//                             {customFieldDefinitions.length > 0 && (
//                                 <div className="existing-fields">
//                                     <h4>Existing Custom Fields</h4>
//                                     {customFieldDefinitions.map(field => (
//                                         <div key={field.id} className="field-definition">
//                                             <div className="field-info">
//                                                 <span><strong>{field.name}</strong> ({field.type})</span>
//                                                 {field.type === 'select' && (
//                                                     <div className="field-options">
//                                                         <label>Options (comma-separated):</label>
//                                                         <input
//                                                             type="text"
//                                                             value={field.options?.join(', ') || ''}
//                                                             onChange={(e) => handleUpdateFieldOptions(field.id, e.target.value)}
//                                                             placeholder="Option 1, Option 2, Option 3"
//                                                         />
//                                                     </div>
//                                                 )}
//                                             </div>
//                                             <button
//                                                 onClick={() => handleRemoveCustomField(field.id)}
//                                                 className="remove-field-btn"
//                                             >
//                                                 Remove
//                                             </button>
//                                         </div>
//                                     ))}
//                                 </div>
//                             )}
//                         </div>
//                     )}

//                     {/* Add New Product Form */}
//                     {showAddForm && (
//                         <div className="add-product-form">
//                             <h3>Add New RTO Product</h3>
//                             <div className="form-row">
//                                 <div className="form-group">
//                                     <label>SKU:</label>
//                                     <input
//                                         type="text"
//                                         value={newProduct.sku}
//                                         onChange={(e) => setNewProduct(prev => ({ ...prev, sku: e.target.value }))}
//                                         placeholder="Enter SKU (e.g., BRAND-001-COLOR)"
//                                     />
//                                 </div>
//                                 <div className="form-group">
//                                     <label>Brand ID:</label>
//                                     <input
//                                         type="text"
//                                         value={newProduct.brandId}
//                                         onChange={(e) => setNewProduct(prev => ({ ...prev, brandId: e.target.value }))}
//                                         placeholder="Enter Brand ID"
//                                     />
//                                 </div>
//                             </div>
//                             <div className="sizes-section">
//                                 <h4>Sizes and Quantities:</h4>
//                                 <div className="sizes-grid">
//                                     {commonSizes.map(size => (
//                                         <div key={size} className="size-input">
//                                             <label>{size}:</label>
//                                             <input
//                                                 type="number"
//                                                 min="0"
//                                                 value={newProduct.sizes[size] || 0}
//                                                 onChange={(e) => handleNewProductSizeChange(size, e.target.value)}
//                                             />
//                                         </div>
//                                     ))}
//                                 </div>
//                             </div>

//                             {/* Custom Fields Section */}
//                             {customFieldDefinitions.length > 0 && (
//                                 <div className="custom-fields-section">
//                                     <h4>Custom Fields:</h4>
//                                     <div className="custom-fields-grid">
//                                         {customFieldDefinitions.map(field => (
//                                             <div key={field.id} className="custom-field-input">
//                                                 <label>{field.name}:</label>
//                                                 {renderCustomFieldInput(
//                                                     field,
//                                                     newProduct.customFields[field.name],
//                                                     handleNewProductCustomFieldChange
//                                                 )}
//                                             </div>
//                                         ))}
//                                     </div>
//                                 </div>
//                             )}
//                             <div className="form-actions">
//                                 <button onClick={handleAddNewProduct} className="save-btn">
//                                     Add Product
//                                 </button>
//                                 <button onClick={() => setShowAddForm(false)} className="cancel-btn">
//                                     Cancel
//                                 </button>
//                             </div>
//                         </div>
//                     )}

//                     {/* Filters Section */}
//                     <div className="filters-section">
//                         <div className="filters-container">
//                             <div className="filter-group">
//                                 <label htmlFor="brandFilter">Filter by Brand:</label>
//                                 <select
//                                     id="brandFilter"
//                                     value={selectedBrand}
//                                     onChange={handleBrandFilterChange}
//                                     className="filter-select"
//                                 >
//                                     <option value="all">All Brands</option>
//                                     {brands.map(brand => (
//                                         <option key={brand} value={brand}>
//                                             {brand}
//                                         </option>
//                                     ))}
//                                 </select>
//                             </div>

//                             <div className="filter-group">
//                                 <label htmlFor="searchSku">Search SKU:</label>
//                                 <input
//                                     id="searchSku"
//                                     type="text"
//                                     value={searchTerm}
//                                     onChange={handleSearchChange}
//                                     placeholder="Enter SKU to search..."
//                                     className="filter-input"
//                                 />
//                             </div>

//                             <div className="filter-group">
//                                 <label>
//                                     <input
//                                         type="checkbox"
//                                         checked={showZeroInventory}
//                                         onChange={(e) => setShowZeroInventory(e.target.checked)}
//                                     />
//                                     Show Zero Inventory
//                                 </label>
//                             </div>

//                             <button onClick={clearFilters} className="clear-filters-btn">
//                                 Clear Filters
//                             </button>
//                         </div>

//                         <div className="inventory-stats">
//                             Showing {Object.keys(filteredInventory).length} items 
//                             | Total Inventory Count: {getTotalInventoryCount()}
//                         </div>
//                     </div>

//                     {loading ? (
//                         <div className="loading-message">
//                             <p>Loading all inventory...</p>
//                         </div>
//                     ) : (
//                         <div className="inventory-cards">
//                             {Object.keys(filteredInventory).length > 0 ? (
//                                 Object.values(filteredInventory).map(item => (
//                                     <div 
//                                         key={item.sku} 
//                                         className={`inventory-card ${deleting === item.sku ? 'deleting' : ''} ${hasZeroInventory(item) ? 'zero-inventory' : ''}`}
//                                     >
//                                         {/* Action Buttons */}
//                                         <div className="action-buttons">
//                                             <button
//                                                 onClick={() => editingItem === item.sku ? handleEditCancel() : handleEditStart(item.sku)}
//                                                 disabled={deleting === item.sku}
//                                                 className="edit-btn"
//                                             >
//                                                 {editingItem === item.sku ? 'Cancel' : 'Edit'}
//                                             </button>
//                                             <button
//                                                 onClick={() => handleDeleteInventoryItem(item.sku, item.docId)}
//                                                 disabled={deleting === item.sku || editingItem === item.sku}
//                                                 className="delete-btn"
//                                             >
//                                                 {deleting === item.sku ? 'Deleting...' : 'Delete'}
//                                             </button>
//                                         </div>

//                                         <div className="inventory-images">
//                                             <div className="image-container">
//                                                 {item.frontImage ? (
//                                                     <img src={item.frontImage} alt="front" className="image" />
//                                                 ) : (
//                                                     <p>No front image</p>
//                                                 )}
//                                             </div>
//                                             <div className="image-container">
//                                                 {item.backImage ? (
//                                                     <img src={item.backImage} alt="back" className="image" />
//                                                 ) : (
//                                                     <p>No back image</p>
//                                                 )}
//                                             </div>
//                                         </div>
                                        
//                                             <div className="inventory-details">
//                                                 <p><strong>SKU:</strong> {item.sku}</p>
//                                                 <p><strong>Brand ID:</strong> {item.brandId}</p>
                                                
//                                                 {/* Display Custom Fields */}
//                                                 {item.customFields && Object.keys(item.customFields).length > 0 && (
//                                                     <div className="custom-fields-display">
//                                                         <h5>Additional Information:</h5>
//                                                         {Object.entries(item.customFields).map(([fieldName, value]) => (
//                                                             <p key={fieldName}>
//                                                                 <strong>{fieldName}:</strong> {
//                                                                     typeof value === 'boolean' 
//                                                                         ? (value ? 'Yes' : 'No')
//                                                                         : value?.toString() || 'N/A'
//                                                                 }
//                                                             </p>
//                                                         ))}
//                                                     </div>
//                                                 )}
                                                
//                                                 {editingItem === item.sku ? (
//                                                 <div className="edit-mode">
//                                                     <div className="size-counts-edit">
//                                                         {Object.keys(editValues).map(size => (
//                                                             <div key={size} className="size-edit-row">
//                                                                 <label><strong>{size}:</strong></label>
//                                                                 <input
//                                                                     type="number"
//                                                                     min="0"
//                                                                     value={editValues[size]}
//                                                                     onChange={(e) => handleEditValueChange(size, e.target.value)}
//                                                                 />
//                                                                 <button
//                                                                     onClick={() => handleDeleteSize(item.sku, size)}
//                                                                     className="delete-size-btn"
//                                                                 >
//                                                                     ×
//                                                                 </button>
//                                                             </div>
//                                                         ))}
//                                                     </div>
                                                    
//                                                     {/* Edit Custom Fields */}
//                                                     {customFieldDefinitions.length > 0 && (
//                                                         <div className="custom-fields-edit">
//                                                             <h5>Custom Fields:</h5>
//                                                             {customFieldDefinitions.map(field => (
//                                                                 <div key={field.id} className="custom-field-edit-row">
//                                                                     <label><strong>{field.name}:</strong></label>
//                                                                     {renderCustomFieldInput(
//                                                                         field,
//                                                                         item.customFields?.[field.name],
//                                                                         (fieldName, value, fieldType) => {
//                                                                             // Update custom fields in edit mode
//                                                                             const updatedInventory = { ...inventory };
//                                                                             if (!updatedInventory[item.sku].customFields) {
//                                                                                 updatedInventory[item.sku].customFields = {};
//                                                                             }
                                                                            
//                                                                             let processedValue = value;
//                                                                             switch (fieldType) {
//                                                                                 case 'number':
//                                                                                     processedValue = parseFloat(value) || 0;
//                                                                                     break;
//                                                                                 case 'boolean':
//                                                                                     processedValue = value === 'true';
//                                                                                     break;
//                                                                                 default:
//                                                                                     processedValue = value;
//                                                                             }
                                                                            
//                                                                             updatedInventory[item.sku].customFields[fieldName] = processedValue;
//                                                                             setInventory(updatedInventory);
//                                                                         }
//                                                                     )}
//                                                                 </div>
//                                                             ))}
//                                                         </div>
//                                                     )}
                                                    
//                                                     <div className="add-size-section">
//                                                         <select
//                                                             onChange={(e) => {
//                                                                 if (e.target.value) {
//                                                                     handleAddNewSize(item.sku, e.target.value);
//                                                                     e.target.value = '';
//                                                                 }
//                                                             }}
//                                                         >
//                                                             <option value="">Add Size</option>
//                                                             {commonSizes
//                                                                 .filter(size => !editValues.hasOwnProperty(size))
//                                                                 .map(size => (
//                                                                     <option key={size} value={size}>{size}</option>
//                                                                 ))
//                                                             }
//                                                         </select>
//                                                     </div>
//                                                     <div className="edit-actions">
//                                                         <button
//                                                             onClick={() => handleEditSave(item.sku)}
//                                                             className="save-btn"
//                                                         >
//                                                             Save
//                                                         </button>
//                                                         <button
//                                                             onClick={handleEditCancel}
//                                                             className="cancel-btn"
//                                                         >
//                                                             Cancel
//                                                         </button>
//                                                     </div>
//                                                 </div>
//                                             ) : (
//                                                 <div className="size-counts">
//                                                     {Object.keys(item.counts).map(size => (
//                                                         <p key={size} className={item.counts[size] === 0 ? 'zero-count' : ''}>
//                                                             <strong>{size}:</strong> {item.counts[size]}
//                                                         </p>
//                                                     ))}
//                                                 </div>
//                                             )}
//                                         </div>
//                                     </div>
//                                 ))
//                             ) : (
//                                 <div className="empty-state">
//                                     <p>No RTO inventory found matching the current filters.</p>
//                                     {(selectedBrand !== 'all' || searchTerm.trim() || !showZeroInventory) && (
//                                         <button onClick={clearFilters} className="show-all-btn">
//                                             Clear Filters to Show All
//                                         </button>
//                                     )}
//                                 </div>
//                             )}
//                         </div>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default AdminRTOInventoryPanel;

// import React, { useEffect, useState } from 'react';
// import { collection, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';
// import { db } from '../firebase';
// import { getAuth } from 'firebase/auth';
// import '../style/RTOinventory.css';

// const AdminRTOInventoryPanel = () => {
//     const [inventory, setInventory] = useState({});
//     const [filteredInventory, setFilteredInventory] = useState({});
//     const [loading, setLoading] = useState(true);
//     const [brands, setBrands] = useState([]);
//     const [selectedBrand, setSelectedBrand] = useState('all');
//     const [showAddForm, setShowAddForm] = useState(false);
//     const [newProduct, setNewProduct] = useState({
//         sku: '',
//         brandId: '',
//         sizes: {}
//     });
//     const [editingItem, setEditingItem] = useState(null);
//     const [customField, setCustomField] = useState({ name: '', value: '' });

//     const commonSizes = ['XS-black', 'XS-white', 'S-black', 'S-white', 'M-black', 'M-white', 'L-black', 'L-white', 'XL-black', 'XL-white', 'XXL-black', 'XXL-white'];

//     const fetchRTOInventory = async () => {
//         try {
//             const inventoryRef = collection(db, 'RTO_inventory');
//             const inventorySnapshot = await getDocs(inventoryRef);
//             const allInventory = {};
//             const uniqueBrands = new Set();

//             inventorySnapshot.forEach(docSnap => {
//                 const skuData = docSnap.data();
//                 const skuPrefix = docSnap.id;
//                 const brandId = skuData.brandId;
                
//                 if (brandId) uniqueBrands.add(brandId);
                
//                 allInventory[skuPrefix] = {
//                     sku: skuPrefix,
//                     brandId: brandId,
//                     sizes: {},
//                     customFields: {}
//                 };

//                 Object.keys(skuData).forEach(key => {
//                     if (key === 'brandId') {
//                         // Skip brandId
//                     } else if (commonSizes.includes(key)) {
//                         allInventory[skuPrefix].sizes[key] = skuData[key] || 0;
//                     } else {
//                         allInventory[skuPrefix].customFields[key] = skuData[key];
//                     }
//                 });
//             });

//             setInventory(allInventory);
//             setFilteredInventory(allInventory);
//             setBrands(Array.from(uniqueBrands));
//             setLoading(false);
//         } catch (error) {
//             console.error('Error fetching inventory:', error);
//             setLoading(false);
//         }
//     };

//     const handleAddProduct = async () => {
//         if (!newProduct.sku || !newProduct.brandId) {
//             alert('Please fill in SKU and Brand ID');
//             return;
//         }

//         try {
//             const docRef = doc(db, 'RTO_inventory', newProduct.sku);
//             const productData = {
//                 brandId: newProduct.brandId,
//                 ...newProduct.sizes
//             };

//             await setDoc(docRef, productData);

//             const updatedInventory = {
//                 ...inventory,
//                 [newProduct.sku]: {
//                     sku: newProduct.sku,
//                     brandId: newProduct.brandId,
//                     sizes: { ...newProduct.sizes },
//                     customFields: {}
//                 }
//             };

//             setInventory(updatedInventory);
//             setNewProduct({ sku: '', brandId: '', sizes: {} });
//             setShowAddForm(false);
//             applyFilters();
//         } catch (error) {
//             console.error('Error adding product:', error);
//             alert('Failed to add product');
//         }
//     };

//     const handleAddCustomField = async (sku) => {
//         if (!customField.name || !customField.value) {
//             alert('Please enter field name and value');
//             return;
//         }

//         try {
//             const itemRef = doc(db, 'RTO_inventory', sku);
//             await updateDoc(itemRef, {
//                 [customField.name]: customField.value
//             });

//             const updatedInventory = { ...inventory };
//             updatedInventory[sku].customFields[customField.name] = customField.value;
//             setInventory(updatedInventory);
            
//             setCustomField({ name: '', value: '' });
//             setEditingItem(null);
//             applyFilters();
//         } catch (error) {
//             console.error('Error adding custom field:', error);
//             alert('Failed to add custom field');
//         }
//     };

//     const applyFilters = () => {
//         let filtered = { ...inventory };

//         if (selectedBrand !== 'all') {
//             filtered = Object.keys(filtered).reduce((acc, key) => {
//                 if (filtered[key].brandId === selectedBrand) {
//                     acc[key] = filtered[key];
//                 }
//                 return acc;
//             }, {});
//         }

//         setFilteredInventory(filtered);
//     };

//     useEffect(() => {
//         fetchRTOInventory();
//     }, []);

//     useEffect(() => {
//         applyFilters();
//     }, [selectedBrand, inventory]);

//     return (
//         <div className="dashboard">
//             <div className="dashboard-container">
//                 <div className="main-content">
//                     <h1>RTO Inventory Management</h1>

//                     {/* Add Product Button */}
//                     <button 
//                         onClick={() => setShowAddForm(!showAddForm)} 
//                         className="add-product-btn"
//                     >
//                         {showAddForm ? 'Cancel' : 'Add Product'}
//                     </button>

//                     {/* Add Product Form */}
//                     {showAddForm && (
//                         <div className="add-product-form">
//                             <h3>Add New Product</h3>
//                             <div className="form-row">
//                                 <input
//                                     type="text"
//                                     placeholder="SKU"
//                                     value={newProduct.sku}
//                                     onChange={(e) => setNewProduct(prev => ({ ...prev, sku: e.target.value }))}
//                                 />
//                                 <input
//                                     type="text"
//                                     placeholder="Brand ID"
//                                     value={newProduct.brandId}
//                                     onChange={(e) => setNewProduct(prev => ({ ...prev, brandId: e.target.value }))}
//                                 />
//                             </div>
//                             <div className="sizes-section">
//                                 <h4>Sizes:</h4>
//                                 {commonSizes.map(size => (
//                                     <div key={size} className="size-input">
//                                         <label>{size}:</label>
//                                         <input
//                                             type="number"
//                                             min="0"
//                                             value={newProduct.sizes[size] || 0}
//                                             onChange={(e) => setNewProduct(prev => ({
//                                                 ...prev,
//                                                 sizes: { ...prev.sizes, [size]: parseInt(e.target.value) || 0 }
//                                             }))}
//                                         />
//                                     </div>
//                                 ))}
//                             </div>
//                             <button onClick={handleAddProduct} className="save-btn">
//                                 Add Product
//                             </button>
//                         </div>
//                     )}

//                     {/* Brand Filter */}
//                     <div className="filters-section">
//                         <label>Filter by Brand:</label>
//                         <select
//                             value={selectedBrand}
//                             onChange={(e) => setSelectedBrand(e.target.value)}
//                         >
//                             <option value="all">All Brands</option>
//                             {brands.map(brand => (
//                                 <option key={brand} value={brand}>{brand}</option>
//                             ))}
//                         </select>
//                     </div>

//                     {loading ? (
//                         <p>Loading inventory...</p>
//                     ) : (
//                         <div className="inventory-cards">
//                             {Object.values(filteredInventory).map(item => (
//                                 <div key={item.sku} className="inventory-card">
//                                     <div className="inventory-details">
//                                         <p><strong>SKU:</strong> {item.sku}</p>
//                                         <p><strong>Brand:</strong> {item.brandId}</p>
                                        
//                                         <div className="sizes">
//                                             <h4>Sizes:</h4>
//                                             {Object.entries(item.sizes).map(([size, count]) => (
//                                                 <p key={size}>{size}: {count}</p>
//                                             ))}
//                                         </div>

//                                         {/* Custom Fields */}
//                                         {Object.keys(item.customFields).length > 0 && (
//                                             <div className="custom-fields">
//                                                 <h4>Custom Fields:</h4>
//                                                 {Object.entries(item.customFields).map(([name, value]) => (
//                                                     <p key={name}>{name}: {value}</p>
//                                                 ))}
//                                             </div>
//                                         )}

//                                         {/* Add Custom Field */}
//                                         <button 
//                                             onClick={() => setEditingItem(editingItem === item.sku ? null : item.sku)}
//                                             className="edit-btn"
//                                         >
//                                             {editingItem === item.sku ? 'Cancel' : 'Add Custom Field'}
//                                         </button>

//                                         {editingItem === item.sku && (
//                                             <div className="custom-field-form">
//                                                 <input
//                                                     type="text"
//                                                     placeholder="Field Name"
//                                                     value={customField.name}
//                                                     onChange={(e) => setCustomField(prev => ({ ...prev, name: e.target.value }))}
//                                                 />
//                                                 <input
//                                                     type="text"
//                                                     placeholder="Field Value"
//                                                     value={customField.value}
//                                                     onChange={(e) => setCustomField(prev => ({ ...prev, value: e.target.value }))}
//                                                 />
//                                                 <button 
//                                                     onClick={() => handleAddCustomField(item.sku)}
//                                                     className="save-btn"
//                                                 >
//                                                     Add Field
//                                                 </button>
//                                             </div>
//                                         )}
//                                     </div>
//                                 </div>
//                             ))}
//                         </div>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default AdminRTOInventoryPanel;


// import React, { useEffect, useState } from 'react';
// import { collection, getDocs, doc, setDoc, updateDoc } from 'firebase/firestore';
// import { db } from '../firebase';
// import { getAuth } from 'firebase/auth';
// import '../style/RTOinventory.css';

// const AdminRTOInventoryPanel = () => {
//     const [inventory, setInventory] = useState({});
//     const [filteredInventory, setFilteredInventory] = useState({});
//     const [loading, setLoading] = useState(true);
//     const [brands, setBrands] = useState([]);
//     const [selectedBrand, setSelectedBrand] = useState('all');
//     const [showAddForm, setShowAddForm] = useState(false);
//     const [newProduct, setNewProduct] = useState({
//         sku: '',
//         brandId: '',
//         itemName: '',
//         sizes: {}
//     });
//     const [editingItem, setEditingItem] = useState(null);
//     const [editingProduct, setEditingProduct] = useState(null);
//     const [editProduct, setEditProduct] = useState({
//         sku: '',
//         brandId: '',
//         itemName: '',
//         sizes: {}
//     });
//     const [customField, setCustomField] = useState({ name: '', value: '' });

//     const commonSizes = ['XS-black', 'XS-white', 'S-black', 'S-white', 'M-black', 'M-white', 'L-black', 'L-white', 'XL-black', 'XL-white', 'XXL-black', 'XXL-white'];

//     const fetchRTOInventory = async () => {
//         try {
//             const inventoryRef = collection(db, 'RTO_inventory');
//             const inventorySnapshot = await getDocs(inventoryRef);
//             const allInventory = {};
//             const uniqueBrands = new Set();

//             inventorySnapshot.forEach(docSnap => {
//                 const skuData = docSnap.data();
//                 const skuPrefix = docSnap.id;
//                 const brandId = skuData.brandId;
                
//                 if (brandId) uniqueBrands.add(brandId);
                
//                 allInventory[skuPrefix] = {
//                     sku: skuPrefix,
//                     brandId: brandId,
//                     itemName: skuData.itemName || '',
//                     sizes: {},
//                     customFields: {}
//                 };

//                 Object.keys(skuData).forEach(key => {
//                     if (key === 'brandId' || key === 'itemName') {
//                         // Skip brandId and itemName as they're handled above
//                     } else if (commonSizes.includes(key)) {
//                         allInventory[skuPrefix].sizes[key] = skuData[key] || 0;
//                     } else {
//                         allInventory[skuPrefix].customFields[key] = skuData[key];
//                     }
//                 });
//             });

//             setInventory(allInventory);
//             setFilteredInventory(allInventory);
//             setBrands(Array.from(uniqueBrands));
//             setLoading(false);
//         } catch (error) {
//             console.error('Error fetching inventory:', error);
//             setLoading(false);
//         }
//     };

//     const handleAddProduct = async () => {
//         if (!newProduct.sku || !newProduct.brandId) {
//             alert('Please fill in SKU and Brand ID');
//             return;
//         }

//         try {
//             const docRef = doc(db, 'RTO_inventory', newProduct.sku);
//             const productData = {
//                 brandId: newProduct.brandId,
//                 itemName: newProduct.itemName,
//                 ...newProduct.sizes
//             };

//             await setDoc(docRef, productData);

//             const updatedInventory = {
//                 ...inventory,
//                 [newProduct.sku]: {
//                     sku: newProduct.sku,
//                     brandId: newProduct.brandId,
//                     itemName: newProduct.itemName,
//                     sizes: { ...newProduct.sizes },
//                     customFields: {}
//                 }
//             };

//             setInventory(updatedInventory);
//             setNewProduct({ sku: '', brandId: '', itemName: '', sizes: {} });
//             setShowAddForm(false);
//             applyFilters();
//         } catch (error) {
//             console.error('Error adding product:', error);
//             alert('Failed to add product');
//         }
//     };

//     const handleEditProduct = async () => {
//         if (!editProduct.sku || !editProduct.brandId) {
//             alert('Please fill in SKU and Brand ID');
//             return;
//         }

//         try {
//             const docRef = doc(db, 'RTO_inventory', editProduct.sku);
//             const productData = {
//                 brandId: editProduct.brandId,
//                 itemName: editProduct.itemName,
//                 ...editProduct.sizes
//             };

//             await updateDoc(docRef, productData);

//             const updatedInventory = {
//                 ...inventory,
//                 [editProduct.sku]: {
//                     sku: editProduct.sku,
//                     brandId: editProduct.brandId,
//                     itemName: editProduct.itemName,
//                     sizes: { ...editProduct.sizes },
//                     customFields: inventory[editProduct.sku].customFields
//                 }
//             };

//             setInventory(updatedInventory);
//             setEditingProduct(null);
//             setEditProduct({ sku: '', brandId: '', itemName: '', sizes: {} });
//             applyFilters();
//         } catch (error) {
//             console.error('Error updating product:', error);
//             alert('Failed to update product');
//         }
//     };

//     const startEditingProduct = (item) => {
//         setEditProduct({
//             sku: item.sku,
//             brandId: item.brandId,
//             itemName: item.itemName,
//             sizes: { ...item.sizes }
//         });
//         setEditingProduct(item.sku);
//     };

//     const cancelEditingProduct = () => {
//         setEditingProduct(null);
//         setEditProduct({ sku: '', brandId: '', itemName: '', sizes: {} });
//     };

//     const handleAddCustomField = async (sku) => {
//         if (!customField.name || !customField.value) {
//             alert('Please enter field name and value');
//             return;
//         }

//         try {
//             const itemRef = doc(db, 'RTO_inventory', sku);
//             await updateDoc(itemRef, {
//                 [customField.name]: customField.value
//             });

//             const updatedInventory = { ...inventory };
//             updatedInventory[sku].customFields[customField.name] = customField.value;
//             setInventory(updatedInventory);
            
//             setCustomField({ name: '', value: '' });
//             setEditingItem(null);
//             applyFilters();
//         } catch (error) {
//             console.error('Error adding custom field:', error);
//             alert('Failed to add custom field');
//         }
//     };

//     const applyFilters = () => {
//         let filtered = { ...inventory };

//         if (selectedBrand !== 'all') {
//             filtered = Object.keys(filtered).reduce((acc, key) => {
//                 if (filtered[key].brandId === selectedBrand) {
//                     acc[key] = filtered[key];
//                 }
//                 return acc;
//             }, {});
//         }

//         setFilteredInventory(filtered);
//     };

//     useEffect(() => {
//         fetchRTOInventory();
//     }, []);

//     useEffect(() => {
//         applyFilters();
//     }, [selectedBrand, inventory]);

//     return (
//         <div className="dashboard">
//             <div className="dashboard-container">
//                 <div className="main-content">
//                     <h1>RTO Inventory Management</h1>

//                     {/* Add Product Button */}
//                     <button 
//                         onClick={() => setShowAddForm(!showAddForm)} 
//                         className="add-product-btn"
//                     >
//                         {showAddForm ? 'Cancel' : 'Add Product'}
//                     </button>

//                     {/* Add Product Form */}
//                     {showAddForm && (
//                         <div className="add-product-form">
//                             <h3>Add New Product</h3>
//                             <div className="form-row">
//                                 <input
//                                     type="text"
//                                     placeholder="SKU"
//                                     value={newProduct.sku}
//                                     onChange={(e) => setNewProduct(prev => ({ ...prev, sku: e.target.value }))}
//                                 />
//                                 <input
//                                     type="text"
//                                     placeholder="Brand ID"
//                                     value={newProduct.brandId}
//                                     onChange={(e) => setNewProduct(prev => ({ ...prev, brandId: e.target.value }))}
//                                 />
//                                 <input
//                                     type="text"
//                                     placeholder="Item Name"
//                                     value={newProduct.itemName}
//                                     onChange={(e) => setNewProduct(prev => ({ ...prev, itemName: e.target.value }))}
//                                 />
//                             </div>
//                             <div className="sizes-section">
//                                 <h4>Sizes:</h4>
//                                 {commonSizes.map(size => (
//                                     <div key={size} className="size-input">
//                                         <label>{size}:</label>
//                                         <input
//                                             type="number"
//                                             min="0"
//                                             value={newProduct.sizes[size] || 0}
//                                             onChange={(e) => setNewProduct(prev => ({
//                                                 ...prev,
//                                                 sizes: { ...prev.sizes, [size]: parseInt(e.target.value) || 0 }
//                                             }))}
//                                         />
//                                     </div>
//                                 ))}
//                             </div>
//                             <button onClick={handleAddProduct} className="save-btn">
//                                 Add Product
//                             </button>
//                         </div>
//                     )}

//                     {/* Brand Filter */}
//                     <div className="filters-section">
//                         <label>Filter by Brand:</label>
//                         <select
//                             value={selectedBrand}
//                             onChange={(e) => setSelectedBrand(e.target.value)}
//                         >
//                             <option value="all">All Brands</option>
//                             {brands.map(brand => (
//                                 <option key={brand} value={brand}>{brand}</option>
//                             ))}
//                         </select>
//                     </div>

//                     {loading ? (
//                         <p>Loading inventory...</p>
//                     ) : (
//                         <div className="inventory-cards">
//                             {Object.values(filteredInventory).map(item => (
//                                 <div key={item.sku} className="inventory-card">
//                                     <div className="inventory-details">
//                                         {editingProduct === item.sku ? (
//                                             // Edit Product Form
//                                             <div className="edit-product-form">
//                                                 <h3>Edit Product</h3>
//                                                 <div className="form-row">
//                                                     <input
//                                                         type="text"
//                                                         placeholder="SKU"
//                                                         value={editProduct.sku}
//                                                         onChange={(e) => setEditProduct(prev => ({ ...prev, sku: e.target.value }))}
//                                                         disabled
//                                                     />
//                                                     <input
//                                                         type="text"
//                                                         placeholder="Brand ID"
//                                                         value={editProduct.brandId}
//                                                         onChange={(e) => setEditProduct(prev => ({ ...prev, brandId: e.target.value }))}
//                                                     />
//                                                     <input
//                                                         type="text"
//                                                         placeholder="Item Name"
//                                                         value={editProduct.itemName}
//                                                         onChange={(e) => setEditProduct(prev => ({ ...prev, itemName: e.target.value }))}
//                                                     />
//                                                 </div>
//                                                 <div className="sizes-section">
//                                                     <h4>Sizes:</h4>
//                                                     {commonSizes.map(size => (
//                                                         <div key={size} className="size-input">
//                                                             <label>{size}:</label>
//                                                             <input
//                                                                 type="number"
//                                                                 min="0"
//                                                                 value={editProduct.sizes[size] || 0}
//                                                                 onChange={(e) => setEditProduct(prev => ({
//                                                                     ...prev,
//                                                                     sizes: { ...prev.sizes, [size]: parseInt(e.target.value) || 0 }
//                                                                 }))}
//                                                             />
//                                                         </div>
//                                                     ))}
//                                                 </div>
//                                                 <div className="edit-buttons">
//                                                     <button onClick={handleEditProduct} className="save-btn">
//                                                         Save Changes
//                                                     </button>
//                                                     <button onClick={cancelEditingProduct} className="cancel-btn">
//                                                         Cancel
//                                                     </button>
//                                                 </div>
//                                             </div>
//                                         ) : (
//                                             // Display Product Details
//                                             <>
//                                                 <p><strong>SKU:</strong> {item.sku}</p>
//                                                 <p><strong>Brand:</strong> {item.brandId}</p>
//                                                 {item.itemName && <p><strong>Item Name:</strong> {item.itemName}</p>}
                                                
//                                                 <div className="sizes">
//                                                     <h4>Sizes:</h4>
//                                                     {Object.entries(item.sizes).map(([size, count]) => (
//                                                         <p key={size}>{size}: {count}</p>
//                                                     ))}
//                                                 </div>

//                                                 {/* Custom Fields */}
//                                                 {Object.keys(item.customFields).length > 0 && (
//                                                     <div className="custom-fields">
//                                                         <h4>Custom Fields:</h4>
//                                                         {Object.entries(item.customFields).map(([name, value]) => (
//                                                             <p key={name}>{name}: {value}</p>
//                                                         ))}
//                                                     </div>
//                                                 )}

//                                                 {/* Action Buttons */}
//                                                 <div className="action-buttons">
//                                                     <button 
//                                                         onClick={() => startEditingProduct(item)}
//                                                         className="edit-btn"
//                                                     >
//                                                         Edit Product
//                                                     </button>
                                                    
//                                                     <button 
//                                                         onClick={() => setEditingItem(editingItem === item.sku ? null : item.sku)}
//                                                         className="edit-btn"
//                                                     >
//                                                         {editingItem === item.sku ? 'Cancel' : 'Add Custom Field'}
//                                                     </button>
//                                                 </div>

//                                                 {editingItem === item.sku && (
//                                                     <div className="custom-field-form">
//                                                         <input
//                                                             type="text"
//                                                             placeholder="Field Name"
//                                                             value={customField.name}
//                                                             onChange={(e) => setCustomField(prev => ({ ...prev, name: e.target.value }))}
//                                                         />
//                                                         <input
//                                                             type="text"
//                                                             placeholder="Field Value"
//                                                             value={customField.value}
//                                                             onChange={(e) => setCustomField(prev => ({ ...prev, value: e.target.value }))}
//                                                         />
//                                                         <button 
//                                                             onClick={() => handleAddCustomField(item.sku)}
//                                                             className="save-btn"
//                                                         >
//                                                             Add Field
//                                                         </button>
//                                                     </div>
//                                                 )}
//                                             </>
//                                         )}
//                                     </div>
//                                 </div>
//                             ))}
//                         </div>
//                     )}
//                 </div>
//             </div>
//         </div>
//     );
// };

// export default AdminRTOInventoryPanel;